import fetchJsonp from 'fetch-jsonp';

import { useTravelataAppConfig } from '@/shared/composables/useTravelataAppConfig';
import type { Country } from '@/features/destination';
import type { DepartureCity } from '@/features/departure-city';
import { adaptCountries, adaptDepartureCities } from './getSearchFormData.adapter';
import { buildDefaultCriteriaIndex } from '../lib/defaultCriteria';
import type { DefaultCriteriaIndex } from '../lib/defaultCriteria';
import type { FormDataResponse } from './getSearchFormData.types';

export interface SearchFormData {
  countries: Country[];
  cities: DepartureCity[];
  defaultCriteria: DefaultCriteriaIndex;
}

const inFlight = new Map<string, Promise<FormDataResponse>>();

export function clearSearchFormDataCache(): void {
  inFlight.clear();
}

export function fetchSearchFormData(formDataUrl?: string): Promise<FormDataResponse> {
  const config = useTravelataAppConfig();
  const url = formDataUrl || config.getFormData;
  const cached = inFlight.get(url);
  if (cached) {
    return cached;
  }
  const request: Promise<FormDataResponse> = requestFormData(url).catch(() => {
    if (inFlight.get(url) === request) {
      inFlight.delete(url);
    }
    return {};
  });
  inFlight.set(url, request);
  return request;
}

async function requestFormData(formDataUrl?: string): Promise<FormDataResponse> {
  const config = useTravelataAppConfig();
  const url = `${config.gatewayUrl}${formDataUrl}`;

  const response = await fetchJsonp(url);
  return response.json<FormDataResponse>();
}

export async function getSearchFormData(): Promise<SearchFormData> {
  const body = await fetchSearchFormData();
  const data = body.data ?? {};
  const positions = data.destinationListPositions ?? [];

  return {
    countries: adaptCountries(positions),
    cities: adaptDepartureCities(data.departureCities ?? []),
    defaultCriteria: buildDefaultCriteriaIndex(positions),
  };
}
